import { useState } from 'react'
import {
  Folder,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  Check,
  X,
  Plus,
  FolderInput,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useNewsStore } from '../stores/newsStore'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GroupManager Props
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface GroupManagerProps {
  onSearchGroups?: (keywords: string[]) => void
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Color Palette for Groups
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const GROUP_COLORS = [
  { value: 'blue', label: '파랑', className: 'bg-blue-500' },
  { value: 'green', label: '초록', className: 'bg-green-500' },
  { value: 'purple', label: '보라', className: 'bg-purple-500' },
  { value: 'orange', label: '주황', className: 'bg-orange-500' },
  { value: 'pink', label: '분홍', className: 'bg-pink-500' },
  { value: 'teal', label: '청록', className: 'bg-teal-500' },
  { value: 'red', label: '빨강', className: 'bg-red-500' },
  { value: 'yellow', label: '노랑', className: 'bg-yellow-500' },
]

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GroupManager Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function GroupManager({ onSearchGroups }: GroupManagerProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupColor, setNewGroupColor] = useState('blue')
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [editingGroupName, setEditingGroupName] = useState('')
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set())

  const {
    groups,
    keywords,
    createGroup,
    updateGroup,
    deleteGroup,
    toggleGroupCollapse,
    addKeywordToGroup,
    removeKeywordFromGroup,
  } = useNewsStore()

  const handleCreateGroup = () => {
    if (newGroupName.trim()) {
      createGroup(newGroupName.trim(), newGroupColor)
      setNewGroupName('')
      setNewGroupColor('blue')
      setIsCreateDialogOpen(false)
    }
  }

  const handleStartEdit = (groupId: string, currentName: string) => {
    setEditingGroupId(groupId)
    setEditingGroupName(currentName)
  }

  const handleSaveEdit = (groupId: string) => {
    if (editingGroupName.trim()) {
      updateGroup(groupId, { name: editingGroupName.trim() })
    }
    setEditingGroupId(null)
    setEditingGroupName('')
  }

  const handleCancelEdit = () => {
    setEditingGroupId(null)
    setEditingGroupName('')
  }

  const handleDeleteGroup = (groupId: string) => {
    deleteGroup(groupId)
  }

  const handleSearchGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId)
    if (group && group.keywords.length > 0 && onSearchGroups) {
      onSearchGroups(group.keywords)
    }
  }

  const handleToggleGroupSelection = (groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const handleSearchSelectedGroups = () => {
    if (selectedGroupIds.size === 0) return

    // Collect all keywords from selected groups (unique)
    const allKeywords = new Set<string>()
    groups.forEach((group) => {
      if (selectedGroupIds.has(group.id)) {
        group.keywords.forEach((keyword) => allKeywords.add(keyword))
      }
    })

    if (allKeywords.size > 0 && onSearchGroups) {
      onSearchGroups(Array.from(allKeywords))
      // Clear selection after search
      setSelectedGroupIds(new Set())
    }
  }

  // Get ungrouped keywords
  const ungroupedKeywords = keywords.filter(
    (keyword) => !groups.some((group) => group.keywords.includes(keyword))
  )

  return (
    <div className="space-y-3">
      {/* Header with Create and Search Buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">키워드 그룹</h3>
        <div className="flex gap-1">
          {selectedGroupIds.size > 0 && onSearchGroups && (
            <Button
              variant="default"
              size="sm"
              className="h-7 gap-1 px-2"
              onClick={handleSearchSelectedGroups}
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs">검색 ({selectedGroupIds.size})</span>
            </Button>
          )}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2">
                <FolderPlus className="h-3.5 w-3.5" />
                <span className="text-xs">그룹 생성</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>새 그룹 생성</DialogTitle>
                <DialogDescription>키워드를 분류할 그룹을 생성하세요.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="group-name">그룹 이름</Label>
                  <Input
                    id="group-name"
                    placeholder="예: 경제 뉴스"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleCreateGroup()
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>색상</Label>
                  <div className="flex flex-wrap gap-2">
                    {GROUP_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setNewGroupColor(color.value)}
                        className={`h-8 w-8 rounded-full ${color.className} transition-transform hover:scale-110 ${
                          newGroupColor === color.value ? 'ring-2 ring-primary ring-offset-2' : ''
                        }`}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  취소
                </Button>
                <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
                  생성
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Groups List */}
      <div className="space-y-2">
        {groups.length === 0 && ungroupedKeywords.length === 0 ? (
          <div className="rounded-lg border border-dashed p-4 text-center">
            <p className="text-xs text-muted-foreground">그룹을 생성하고 키워드를 정리하세요</p>
          </div>
        ) : (
          <>
            {/* Existing Groups */}
            {groups.map((group) => {
              const colorClass =
                GROUP_COLORS.find((c) => c.value === group.color)?.className || 'bg-gray-500'

              return (
                <div
                  key={group.id}
                  className="rounded-lg border bg-card transition-colors hover:bg-accent/50"
                >
                  {/* Group Header */}
                  <div className="flex items-center gap-2 p-2">
                    {onSearchGroups && (
                      <Checkbox
                        checked={selectedGroupIds.has(group.id)}
                        onCheckedChange={() => handleToggleGroupSelection(group.id)}
                        className="h-3.5 w-3.5"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleGroupCollapse(group.id)}
                    >
                      {group.collapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <div className={`h-4 w-4 rounded-full ${colorClass}`} />
                    {editingGroupId === group.id ? (
                      <div className="flex flex-1 items-center gap-1">
                        <Input
                          value={editingGroupName}
                          onChange={(e) => setEditingGroupName(e.target.value)}
                          className="h-7 flex-1 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleSaveEdit(group.id)
                            } else if (e.key === 'Escape') {
                              handleCancelEdit()
                            }
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleSaveEdit(group.id)}
                        >
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={handleCancelEdit}
                        >
                          <X className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium">{group.name}</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {group.keywords.length}
                        </span>
                        {onSearchGroups && group.keywords.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleSearchGroup(group.id)}
                            title="이 그룹 검색"
                          >
                            <Search className="h-3 w-3" />
                          </Button>
                        )}
                        {ungroupedKeywords.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                title="키워드 추가"
                              >
                                <FolderInput className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel className="text-xs">키워드 추가</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {ungroupedKeywords.map((keyword) => (
                                <DropdownMenuItem
                                  key={keyword}
                                  onClick={() => addKeywordToGroup(group.id, keyword)}
                                  className="cursor-pointer text-xs"
                                >
                                  {keyword}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleStartEdit(group.id, group.name)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive"
                              aria-label={`${group.name} 그룹 삭제`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>그룹 삭제</AlertDialogTitle>
                              <AlertDialogDescription>
                                &lsquo;{group.name}&rsquo; 그룹을 삭제하시겠습니까? 그룹만 사라지고
                                키워드는 유지됩니다.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>취소</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteGroup(group.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                삭제
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>

                  {/* Group Keywords */}
                  {!group.collapsed && (
                    <div className="border-t px-2 pb-2 pt-1">
                      {group.keywords.length === 0 ? (
                        <p className="py-2 text-center text-xs text-muted-foreground">
                          이 그룹에 키워드가 없습니다
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {group.keywords.map((keyword) => (
                            <div
                              key={keyword}
                              className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs"
                            >
                              <span>{keyword}</span>
                              <button
                                onClick={() => removeKeywordFromGroup(group.id, keyword)}
                                className="rounded-full p-0.5 hover:bg-secondary-foreground/20"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Ungrouped Keywords */}
            {ungroupedKeywords.length > 0 && (
              <div className="rounded-lg border bg-card/50">
                <div className="flex items-center gap-2 p-2">
                  <Folder className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-sm font-medium text-muted-foreground">미분류</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {ungroupedKeywords.length}
                  </span>
                </div>
                <div className="border-t px-2 pb-2 pt-1">
                  <div className="flex flex-wrap gap-1">
                    {ungroupedKeywords.map((keyword) => (
                      <DropdownMenu key={keyword}>
                        <DropdownMenuTrigger asChild>
                          <button className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs transition-colors hover:bg-accent hover:text-accent-foreground">
                            <span>{keyword}</span>
                            <Plus className="h-3 w-3 opacity-60" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel className="text-xs">그룹에 추가</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {groups.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">
                              그룹이 없습니다
                            </div>
                          ) : (
                            groups.map((group) => {
                              const colorClass =
                                GROUP_COLORS.find((c) => c.value === group.color)?.className ||
                                'bg-gray-500'
                              return (
                                <DropdownMenuItem
                                  key={group.id}
                                  onClick={() => addKeywordToGroup(group.id, keyword)}
                                  className="cursor-pointer text-xs"
                                >
                                  <div className={`mr-2 h-3 w-3 rounded-full ${colorClass}`} />
                                  <span>{group.name}</span>
                                </DropdownMenuItem>
                              )
                            })
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
